import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, componentAutoReaction, IDisposable, inject } from 'Mri.Web.Common';
import * as React from 'react';
import { Route, RouteComponentProps } from 'react-router';
import { ContractsStore } from '../../Contracts/ContractsStore';
import { AppStore } from '../../Core/AppStore';
import { DisplayAuditLog } from './DisplayAuditLog';
import { DisplayConfigLog } from './DisplayConfigLog';
import { DisplayEventLogList } from './DisplayEventLogList';
import { DisplaysStore } from '../DisplaysStore';
import { DisplayStatus } from './DisplayStatus';
import { DisplayStatusLog } from './DisplayStatusLog';
import { DisplayViewNav } from './DisplayViewNav';
import { DisplayViewTitle } from './DisplayViewTitle';
import { DisplayLiveChartsView } from './LiveCharts/DisplayLiveChartsView';
import { MinMaxList } from './MinMaxList';
import { TelemetryChartsView } from './TelemetryChartsView';
import { TelemetryListView } from './TelemetryListView';
import { DisplaySettingsView } from './Settings/DisplaySettingsView';
import { DisplayEventLogCards } from './DisplayEventLogCards';
import { PartsStore } from '../../Parts/PartsStore';
import {DisplayWarrantyAlertLog} from "./DisplayWarrantyAlertLog";
import {DisplayInternalAlertLog} from "./DisplayInternalAlertLog";
import {DisplayEventStatusChangeLog} from "./DisplayEventStatusChangeLog";

export interface IDisplayViewParams {
    serialNumber: string;
}
export interface IDisplayViewProps extends RouteComponentProps<IDisplayViewParams> {}

@autoinject
@observer
export class DisplayView extends React.Component<IDisplayViewProps> {
    @action
    public async componentDidMount() {
        componentAutoReaction(this, () => this.props.match.params.serialNumber, this.loadAsync);
    }

    public async componentWillUnmount() {
        if (this.refreshTimer) window.clearTimeout(this.refreshTimer);
        this.handle?.dispose();
        this.handle = undefined;
    }

    public render() {
        return (
            <>
                <DisplayViewTitle serialNumber={this.props.match.params.serialNumber} />
                <DisplayViewNav serialNumber={this.props.match.params.serialNumber} />

                <Route path="/displays/:serialNumber" exact component={DisplayStatus} />
                <Route path="/displays/:serialNumber/telemetry/chart" exact component={TelemetryChartsView} />
                <Route path="/displays/:serialNumber/telemetry/table" exact component={TelemetryListView} />
                <Route path="/displays/:serialNumber/min-max" exact component={MinMaxList} />
                <Route path="/displays/:serialNumber/live-charts" exact component={DisplayLiveChartsView} />
                <Route path="/displays/:serialNumber/status-log" exact component={DisplayStatusLog} />
                <Route path="/displays/:serialNumber/event-log/list" exact component={DisplayEventLogList} />
                <Route path="/displays/:serialNumber/event-log/cards" exact component={DisplayEventLogCards} />
                <Route path="/displays/:serialNumber/action-log" component={DisplayAuditLog} />
                <Route path="/displays/:serialNumber/event-status-change-log" component={DisplayEventStatusChangeLog} />
                <Route path="/displays/:serialNumber/warranty-alert-log" component={DisplayWarrantyAlertLog} />
                <Route path="/displays/:serialNumber/internal-alert-log" component={DisplayInternalAlertLog} />
                <Route path="/displays/:serialNumber/config-log" component={DisplayConfigLog} />
                <Route path="/displays/:serialNumber/settings" component={DisplaySettingsView} />
            </>
        );
    }

    @bind
    @action
    private async loadAsync() {
        if (this.refreshTimer) window.clearTimeout(this.refreshTimer);

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber, {
                params: {
                    status: true,
                    contract: true,
                    displayGroups: true,
                },
            });

            if (this.display?.contract) await this.contractsStore.loadAsync(this.display.contract, true);
        } catch (e: any) {
            this.appStore.setError(e);
            return;
        }

        try {
            this.handle = await this.displaysStore.connectionManager.connect();
            await this.displaysStore.subscribeToDisplayAsync(this.props.match.params.serialNumber);
            await this.refresh();
            await this.displaysStore.loadLatestScreenSnapshotAsync(this.props.match.params.serialNumber);
        } catch (e: any) {
            console.error('SubscribeToDisplay', e);
        }

        try {
            await this.displaysStore.loadDisplayLoginAsync(this.props.match.params.serialNumber);
        } catch (e: any) {
            // Non-fatal error
            console.error(e);
        }

        if (this.display) {
            try {
                this.partsStore.loadPartsFromGuid(this.display.unitPartGuid ?? this.display.partGuid, true);
            } catch (e: any) {
                console.error(e);
            }
        }

        if (this.handle) this.refreshTimer = window.setTimeout(this.refresh, 30 * 1000);
    }

    @bind
    @action
    private async refresh() {
        if (this.refreshTimer) window.clearTimeout(this.refreshTimer);

        try {
            await this.displaysStore.refresh(this.props.match.params.serialNumber);
        } catch (e: any) {
            console.error(e);
        }

        if (this.handle) this.refreshTimer = window.setTimeout(this.refresh, 30 * 1000);
    }

    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.match.params.serialNumber);
    }

    private refreshTimer?: number;
    private handle?: IDisposable;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private partsStore!: PartsStore;
    @inject
    private contractsStore!: ContractsStore;
    @inject
    private appStore!: AppStore;
}
